/**
 * FoodPulseUrbanHarvest — Home Fruit & Vegetable Registry (HFVR)
 *
 * Route:   /food-pulse/urban-harvest
 * Backend: /api/urban-harvest/*
 *
 * A single long-scroll page containing:
 *   • Hero + live stats
 *   • "Register a tree" form (public, no login)
 *   • Live registered-tree grid (browse + filter)
 *   • Vision, phased rollout, Civic Bounty programme, next-steps
 *   • FAQ + call-to-action for cities
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Trees, MapPin, Users, Leaf, Loader2, Send, ArrowRight, HelpCircle,
  Sparkles, HandHeart, CheckCircle2, Award, Package, Camera,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

// Warm harvest palette — distinct from the FoodPulse amber palette
// so this section feels like a fresh sibling programme, not a rerun.
const PALETTE = {
  cream:   "#FBF7EF",
  ink:     "#1C3B1E",
  leaf:    "#3E7C4B",
  moss:    "#245635",
  amber:   "#E4A140",
  orange:  "#EA580C",
  peach:   "#FFE9D0",
  sage:    "#DCEAD1",
};


export default function FoodPulseUrbanHarvest() {
  const [stats, setStats] = useState(null);
  const [trees, setTrees] = useState([]);
  const [treeFilter, setTreeFilter] = useState({ city: "", fruit_type: "" });

  useEffect(() => { document.title = "The Urban Harvest — Food Pulse"; }, []);

  useEffect(() => {
    axios.get(`${API}/api/urban-harvest/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (treeFilter.city)       params.city = treeFilter.city;
    if (treeFilter.fruit_type) params.fruit_type = treeFilter.fruit_type;
    params.limit = 30;
    axios.get(`${API}/api/urban-harvest/trees`, { params })
      .then((r) => setTrees(r.data.items || []))
      .catch(() => setTrees([]));
  }, [treeFilter]);

  return (
    <div className="min-h-screen" style={{ background: PALETTE.cream, color: PALETTE.ink }}
         data-testid="urban-harvest-page">
      <SeoHead section="food-pulse"
               title="The Urban Harvest — Food Pulse"
               description="Register your backyard fruit tree. Turn hidden urban orchards into fresh food for your community. Free, one-tap harvest help." />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b"
               style={{ background: `linear-gradient(135deg, ${PALETTE.moss} 0%, ${PALETTE.leaf} 100%)`,
                        borderColor: "#0B2E12" }}>
        <div className="absolute inset-0 pointer-events-none opacity-10"
             style={{
               backgroundImage: "radial-gradient(circle at 20% 30%, #fff 0%, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0%, transparent 40%)",
             }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-white">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-3"
               style={{ color: PALETTE.amber }}>
            <Trees className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            Food Pulse · Urban Harvest
          </div>
          <h1 className="font-display text-4xl sm:text-6xl leading-tight max-w-3xl">
            Your backyard tree or garden can feed a neighbourhood.
          </h1>
          <p className="mt-4 max-w-2xl text-lg" style={{ color: PALETTE.peach }}>
            In Los Angeles, Silicon Valley, Texas, and Florida, thousands of
            pounds of fresh fruit and vegetables go to waste in private yards
            every year — not from scarcity, but from time poverty. The Urban
            Harvest turns hidden orchards and home gardens into a shared
            community resource.
          </p>
          <div className="mt-3 text-sm">
            <Link to="/food-pulse/urban-harvest/story"
                  className="inline-flex items-center gap-1 underline decoration-2 underline-offset-4 font-semibold hover:opacity-80"
                  style={{ color: PALETTE.amber }}
                  data-testid="uh-learn-more">
              Learn more <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Live stats */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl"
               data-testid="uh-hero-stats">
            <HeroStat icon={Trees}
                      label="Trees / Gardens registered"
                      value={stats?.trees}
                      testid="uh-stat-trees" />
            <HeroStat icon={MapPin}
                      label="Cities covered"
                      value={stats?.cities}
                      testid="uh-stat-cities" />
            <HeroStat icon={Package}
                      label="lbs capacity / year"
                      value={stats?.estimated_lbs_capacity}
                      testid="uh-stat-capacity" />
            <HeroStat icon={Users}
                      label="Meals possible"
                      value={stats?.meals_equivalent}
                      testid="uh-stat-meals" />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#register"
               className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold hover:shadow-lg transition-shadow"
               style={{ background: PALETTE.amber, color: PALETTE.moss }}
               data-testid="uh-hero-register-cta">
              Register your tree / garden <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#browse"
               className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold border-2 border-white/40 text-white hover:bg-white/10 transition-colors"
               data-testid="uh-hero-browse-cta">
              Browse the map
            </a>
          </div>
        </div>
      </section>


      {/* ── Vision + Problem ────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid gap-10 md:grid-cols-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                 style={{ color: PALETTE.leaf }}>The problem</div>
            <h2 className="font-display text-2xl mb-3">Fruit isn't scarce — coordination is.</h2>
            <p className="text-sm opacity-80">
              High-cost urban regions grow enormous surpluses that never leave
              private yards. Sidewalks stain, pests multiply, and families two
              blocks away buy imported oranges from a grocery store.
            </p>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                 style={{ color: PALETTE.leaf }}>Our promise</div>
            <h2 className="font-display text-2xl mb-3">One tap. One tree. One neighbourhood.</h2>
            <p className="text-sm opacity-80">
              Register once, forever. FoodPulse wakes your listing up when it's
              peak season and asks a single question: "Ready to share?"
              A busy homeowner never has to lift a ladder again.
            </p>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                 style={{ color: PALETTE.leaf }}>The pay-off</div>
            <h2 className="font-display text-2xl mb-3">Real food. Real community.</h2>
            <p className="text-sm opacity-80">
              Nutrition for neighbours, waste diverted from landfill, sidewalks
              cleared for cities, and social capital rebuilt one harvest party
              at a time.
            </p>
          </div>
        </div>
      </section>


      {/* ── Registration form ──────────────────────────────────── */}
      <section id="register" className="border-y"
               style={{ background: PALETTE.sage, borderColor: "#B9CFA9" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
               style={{ color: PALETTE.moss }}>Smart Registry</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-2">Add your tree or garden in 60 seconds.</h2>
          <p className="opacity-80 mb-6 max-w-2xl">
            Once registered, we'll only ping you when your fruit is close to
            peak. You choose every time whether to share. No pressure, ever.
          </p>
          <TreeRegistrationForm onSuccess={() => {
            // Refetch stats + trees when a new registration lands.
            axios.get(`${API}/api/urban-harvest/stats`).then((r) => setStats(r.data));
            axios.get(`${API}/api/urban-harvest/trees`, { params: { limit: 30 } })
                 .then((r) => setTrees(r.data.items || []));
          }} />
        </div>
      </section>


      {/* ── Harvest modes explained ─────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
             style={{ color: PALETTE.leaf }}>How sharing works</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">You stay in control every season.</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <ModeCard title="Pick your own"
                    slug="pick-your-own"
                    desc="Vetted volunteers arrive at a time you approve, harvest cleanly, and leave."
                    color={PALETTE.leaf} />
          <ModeCard title="Pre-picked curbside"
                    slug="pre-picked-curbside"
                    desc="You harvest into a box, we help distribute — no strangers on your property."
                    color={PALETTE.amber} />
          <ModeCard title="Donation only"
                    slug="donation-only"
                    desc="Food bank or vetted nonprofit picks. Guaranteed hunger relief."
                    color={PALETTE.orange} />
          <ModeCard title="Harvest help needed"
                    slug="harvest-help-needed"
                    desc="One tap. FoodPulse matches nearby volunteers or the Gleaning Corps."
                    color={PALETTE.moss} highlight />
        </div>
      </section>


      {/* ── Suggest-a-Tree / Garden (community lead form) ────────── */}
      <section id="suggest" className="border-t"
               style={{ background: PALETTE.peach, borderColor: "#F0C79A" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
               style={{ color: PALETTE.orange }}>Community leads</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-2">
            Spotted a fruit tree or vegetable garden nearby?
          </h2>
          <p className="opacity-80 mb-6 max-w-2xl">
            You don't have to own the property — just tell us where you've
            seen a productive tree, backyard vegetable patch, or community
            plot. We'll send a friendly invitation to the owner so they
            can decide whether to join the Urban Harvest. Comments accepted
            in any language.
          </p>
          <SuggestTreeOrGardenForm />
        </div>
      </section>


      {/* ── Browse map / list ───────────────────────────────────── */}
      <section id="browse" className="border-t"
               style={{ background: "#F3EFE3", borderColor: "#E9D8B4" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
                   style={{ color: PALETTE.leaf }}>The community canopy</div>
              <h2 className="font-display text-3xl sm:text-4xl">
                {trees.length.toLocaleString()} tree{trees.length === 1 ? "" : "s"} on the map.
              </h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              <input type="text" placeholder="Filter by city"
                     value={treeFilter.city}
                     onChange={(e) => setTreeFilter({ ...treeFilter, city: e.target.value })}
                     className="rounded-full border px-4 py-2 text-sm bg-white"
                     style={{ borderColor: "#C79A3F" }}
                     data-testid="uh-filter-city" />
              <select value={treeFilter.fruit_type}
                      onChange={(e) => setTreeFilter({ ...treeFilter, fruit_type: e.target.value })}
                      className="rounded-full border px-4 py-2 text-sm bg-white"
                      style={{ borderColor: "#C79A3F" }}
                      data-testid="uh-filter-fruit">
                <option value="">All fruits</option>
                {(stats?.top_fruits || []).map((f) =>
                  <option key={f.fruit_type} value={f.fruit_type}>{f.fruit_type} ({f.count})</option>
                )}
              </select>
            </div>
          </div>

          {trees.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed p-10 text-center bg-white"
                 style={{ borderColor: PALETTE.leaf }} data-testid="uh-empty">
              <Trees className="w-8 h-8 mx-auto opacity-40 mb-2" style={{ color: PALETTE.leaf }} />
              <div className="font-display text-lg mb-1">Be the first on the map.</div>
              <p className="text-sm opacity-70 mb-4">Register a tree above to plant the first pin.</p>
              <a href="#register"
                 className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-white font-semibold"
                 style={{ background: PALETTE.leaf }}
                 data-testid="uh-empty-register">
                Register a tree <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="uh-tree-grid">
              {trees.map((t) => <TreeCard key={t.id} t={t} />)}
            </div>
          )}
        </div>
      </section>


      {/* ── Civic Bounty programme ──────────────────────────────── */}
      <section className="border-t"
               style={{ background: PALETTE.ink, borderColor: "#0B2E12", color: "#fff" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
               style={{ color: PALETTE.amber }}>
            <Award className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
            For city partners
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mb-4">
            The Civic Bounty Initiative.
          </h2>
          <p className="max-w-2xl opacity-90 mb-8">
            Cities already pay the cost of wasted fruit — rodent control, sidewalk
            hazards, landfill fees, methane emissions. FoodPulse becomes a
            municipal sustainability partner, converting hidden yield into
            civic value.
          </p>

          <div className="grid gap-6 md:grid-cols-3">
            <CivicCard title="Green Credits" icon={Sparkles}
                       body="Utility-bill discounts, composting-bag credits, or redeemable green points for verified sharing." />
            <CivicCard title="Liability Shield" icon={HandHeart}
                       body="Cities extend Good Samaritan protection to homeowners, volunteers, and FoodPulse for good-faith donations." />
            <CivicCard title="Gleaning Corps" icon={Users}
                       body="City-backed, trained, insured volunteer crews with ladders and tools. FoodPulse handles scheduling and impact reporting." />
          </div>

          <div className="mt-10 rounded-2xl border-2 border-dashed p-6"
               style={{ borderColor: PALETTE.amber, background: "#0F2A15" }}>
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 flex-none mt-0.5" style={{ color: PALETTE.amber }} />
              <div>
                <div className="font-display text-lg mb-1">Are you a City Council or Sustainability lead?</div>
                <p className="text-sm opacity-90">
                  We have a ready-to-send council proposal, 90-day pilot design, and
                  grant-aligned impact reporting. Email{" "}
                  <a href="mailto:urbanharvest@foodpulse.net"
                     className="underline"
                     style={{ color: PALETTE.amber }}
                     data-testid="uh-city-email">
                    urbanharvest@foodpulse.net
                  </a>{" "}
                  to arrange a briefing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ── Phased rollout ─────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
             style={{ color: PALETTE.leaf }}>Rollout</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-8">
          From awareness to a real local economy.
        </h2>
        <div className="grid gap-5 md:grid-cols-3">
          <PhaseCard n="01" title="Map the Bounty"
                     body="No pressure to share yet — just pin your tree so the neighbourhood canopy becomes visible for the first time."
                     color={PALETTE.leaf} />
          <PhaseCard n="02" title="The Harvest Swap"
                     body="Saturday drop-off zones in parks and schools. Local food banks coordinate weekly redistribution."
                     color={PALETTE.amber} />
          <PhaseCard n="03" title="Micro-Sales"
                     body="Homegrown baskets priced below retail. Revenue splits between owner, FoodPulse, and local schools."
                     color={PALETTE.orange} />
        </div>
      </section>


      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section className="border-t"
               style={{ background: PALETTE.peach, borderColor: "#F0C79A" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2"
               style={{ color: PALETTE.orange }}>Questions we hear a lot</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-6">You stay in charge.</h2>
          <dl className="space-y-4">
            <Faq q="Do strangers get my address?" a="Never. Only your city is public. Exact addresses are shared only after you approve a specific harvest." />
            <Faq q="What if I don't want to share this year?" a="Toggle to 'Not sure yet' whenever you like. Registering does not obligate you." />
            <Faq q="Who's liable if someone falls?" a="We're actively lobbying cities to extend Good Samaritan protections. In cities that already have them, homeowners donating in good faith are covered." />
            <Faq q="Can businesses register?" a="Yes — restaurants with urban gardens, farms, and school orchards are all welcome. Use 'Other' as fruit type and describe in access notes." />
          </dl>
        </div>
      </section>
    </div>
  );
}


// ── Registration form ────────────────────────────────────────────
function TreeRegistrationForm({ onSuccess }) {
  const [form, setForm] = useState({
    owner_name: "", email: "", phone: "",
    city: "", state: "", country: "USA", postal_code: "",
    fruit_type: "", variety: "", tree_count: 1, organic: false,
    estimated_yield_lbs: "", tree_age_years: "",
    harvest_mode: "not_sure_yet",
    access_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [fruits, setFruits] = useState([]);
  const [modes, setModes] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/urban-harvest/stats`).then((r) => {
      setFruits(r.data.common_fruits || []);
      setModes(r.data.harvest_modes || {});
    });
  }, []);

  const canSubmit = useMemo(() =>
    form.owner_name.trim().length >= 2 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) &&
    form.city.trim().length >= 1 &&
    form.fruit_type.trim().length >= 2,
  [form]);

  async function submit(e) {
    e?.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        owner_name: form.owner_name.trim(),
        email:      form.email.trim(),
        city:       form.city.trim(),
        fruit_type: form.fruit_type.trim(),
        variety:    form.variety.trim() || null,
        access_notes: form.access_notes.trim() || null,
        tree_count: Number(form.tree_count) || 1,
        estimated_yield_lbs: form.estimated_yield_lbs ? Number(form.estimated_yield_lbs) : null,
        tree_age_years:      form.tree_age_years      ? Number(form.tree_age_years)      : null,
      };
      const res = await axios.post(`${API}/api/urban-harvest/trees`, payload);
      setDone(res.data);
      toast.success("Tree registered.");
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't register your tree. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border-2 bg-white p-6 sm:p-8"
           style={{ borderColor: PALETTE.leaf }} data-testid="uh-registered">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-8 h-8 flex-none" style={{ color: PALETTE.leaf }} />
          <div>
            <div className="font-display text-2xl mb-1" style={{ color: PALETTE.ink }}>
              You're on the map.
            </div>
            <p className="text-sm opacity-80 max-w-lg mb-3">{done.message}</p>
            <div className="text-xs opacity-70">
              We'll notify you when your fruit is close to peak so you can pick your
              sharing mode for the season. You can always update details later.
            </div>
            <button type="button"
                    onClick={() => { setDone(null); setForm({ ...form, fruit_type: "", variety: "" }); }}
                    className="mt-4 text-xs underline opacity-80 hover:opacity-100"
                    data-testid="uh-register-another">
              Register another tree
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}
          className="rounded-2xl border-2 bg-white p-5 sm:p-6 space-y-4"
          style={{ borderColor: PALETTE.leaf }}
          data-testid="uh-register-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Your name">
          <input type="text" required minLength={2} value={form.owner_name}
                 onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                 className="input" placeholder="Priya Iyer"
                 data-testid="uh-name" />
        </Field>
        <Field label="Email">
          <input type="email" required value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="input" placeholder="priya@example.com"
                 data-testid="uh-email" />
        </Field>
        <Field label="Phone (optional)">
          <input type="tel" value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 className="input" placeholder="+1 555 123 4567"
                 data-testid="uh-phone" />
        </Field>
        <Field label="City">
          <input type="text" required value={form.city}
                 onChange={(e) => setForm({ ...form, city: e.target.value })}
                 className="input" placeholder="Sunnyvale"
                 data-testid="uh-city" />
        </Field>
        <Field label="State">
          <input type="text" value={form.state}
                 onChange={(e) => setForm({ ...form, state: e.target.value })}
                 className="input" placeholder="California"
                 data-testid="uh-state" />
        </Field>
        <Field label="Postal code (optional)">
          <input type="text" value={form.postal_code}
                 onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                 className="input" placeholder="94087"
                 data-testid="uh-postal" />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Fruit / Vegetable">
          <select required value={form.fruit_type}
                  onChange={(e) => setForm({ ...form, fruit_type: e.target.value })}
                  className="input" data-testid="uh-fruit">
            <option value="">Choose one…</option>
            {fruits.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Variety (optional)">
          <input type="text" value={form.variety}
                 onChange={(e) => setForm({ ...form, variety: e.target.value })}
                 className="input" placeholder="Fuyu, Valencia, Meyer…"
                 data-testid="uh-variety" />
        </Field>
        <Field label="How many trees?">
          <input type="number" min={1} max={200} value={form.tree_count}
                 onChange={(e) => setForm({ ...form, tree_count: e.target.value })}
                 className="input" data-testid="uh-count" />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Estimated annual yield (lbs, optional)">
          <input type="number" min={0} max={100000} value={form.estimated_yield_lbs}
                 onChange={(e) => setForm({ ...form, estimated_yield_lbs: e.target.value })}
                 className="input" placeholder="e.g. 200"
                 data-testid="uh-yield" />
        </Field>
        <Field label="Tree age (years, optional)">
          <input type="number" min={0} max={200} value={form.tree_age_years}
                 onChange={(e) => setForm({ ...form, tree_age_years: e.target.value })}
                 className="input" placeholder="e.g. 12"
                 data-testid="uh-age" />
        </Field>
      </div>

      <Field label="This season, I'd like to…">
        <select value={form.harvest_mode}
                onChange={(e) => setForm({ ...form, harvest_mode: e.target.value })}
                className="input" data-testid="uh-mode">
          {Object.entries(modes).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
      </Field>

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.organic}
               onChange={(e) => setForm({ ...form, organic: e.target.checked })}
               data-testid="uh-organic" />
        Grown organically (no synthetic pesticides)
      </label>

      <Field label="Access notes (kept private — only shared after you approve a harvester)">
        <textarea rows={3} value={form.access_notes}
                  onChange={(e) => setForm({ ...form, access_notes: e.target.value })}
                  className="input"
                  placeholder="Gate code, best time to visit, dogs on premises, etc."
                  data-testid="uh-notes" />
      </Field>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="text-[10px] opacity-60 max-w-md">
          By registering you agree that FoodPulse can list your tree publicly by
          city and fruit type. Your email, phone, and address stay private.
        </div>
        <button type="submit" disabled={!canSubmit || busy}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-50 hover:shadow-md transition-shadow"
                style={{ background: PALETTE.leaf }}
                data-testid="uh-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Registering…" : "Register my tree / garden"}
        </button>
      </div>

      <style>{`.input{width:100%;border:1px solid #C7B58A;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;background:#fff}`}</style>
    </form>
  );
}


// ── Presentational helpers ───────────────────────────────────────
function HeroStat({ icon: Icon, label, value, testid }) {
  const display = typeof value === "number" ? value.toLocaleString() : (value ?? "—");
  return (
    <div className="rounded-2xl px-4 py-3 backdrop-blur"
         style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)" }}
         data-testid={testid}>
      <Icon className="w-4 h-4 mb-1" style={{ color: PALETTE.amber }} />
      <div className="font-display text-2xl font-bold">{display}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{label}</div>
    </div>
  );
}


function ModeCard({ title, desc, color, highlight, slug }) {
  const testSlug = title.toLowerCase().replace(/\s/g, '-');
  return (
    <div className="rounded-2xl border bg-white p-5 flex flex-col"
         style={{ borderColor: highlight ? color : "#E9D8B4",
                  boxShadow: highlight ? `0 4px 20px ${color}22` : "none" }}
         data-testid={`uh-mode-card-${testSlug}`}>
      <div className="font-display text-lg mb-1" style={{ color }}>
        {title}
      </div>
      <p className="text-sm opacity-80 flex-1">{desc}</p>
      {slug && (
        <Link to={`/food-pulse/urban-harvest/${slug}`}
              data-testid={`uh-mode-learn-more-${slug}`}
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold hover:opacity-80"
              style={{ color: PALETTE.orange }}>
          Learn more <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}


function TreeCard({ t }) {
  const modeColor = {
    pick_your_own: PALETTE.leaf,
    pre_picked: PALETTE.amber,
    donation_only: PALETTE.orange,
    harvest_help_needed: PALETTE.orange,
    not_sure_yet: "#888",
  }[t.harvest_mode] || "#888";

  const modeLabel = {
    pick_your_own: "Pick your own",
    pre_picked: "Pre-picked",
    donation_only: "Donation only",
    harvest_help_needed: "Help needed",
    not_sure_yet: "Registered",
  }[t.harvest_mode] || t.harvest_mode;

  return (
    <div className="rounded-2xl border bg-white p-5 hover:shadow-md transition-all hover:-translate-y-0.5"
         style={{ borderColor: "#E9D8B4" }}
         data-testid={`uh-tree-${t.id}`}>
      <div className="flex items-start justify-between mb-2">
        <Trees className="w-5 h-5" style={{ color: PALETTE.leaf }} />
        <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ background: `${modeColor}22`, color: modeColor }}>
          {modeLabel}
        </span>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">
        {t.city}{t.state ? `, ${t.state}` : ""}
      </div>
      <div className="font-display text-lg leading-tight mt-1" style={{ color: PALETTE.ink }}>
        {t.tree_count > 1 && <span className="opacity-70">{t.tree_count}× </span>}
        {t.fruit_type}
      </div>
      {t.variety && <div className="text-sm opacity-80 italic">{t.variety}</div>}
      <div className="mt-2 flex flex-wrap gap-1 text-[10px] font-mono opacity-70">
        {t.organic && <span className="px-2 py-0.5 rounded-full"
                            style={{ background: PALETTE.sage, color: PALETTE.moss }}>Organic</span>}
        {t.estimated_yield_lbs && <span>~{t.estimated_yield_lbs} lbs / yr</span>}
        {t.tree_age_years && <span>· {t.tree_age_years} yrs old</span>}
      </div>
    </div>
  );
}


function CivicCard({ title, body, icon: Icon }) {
  return (
    <div className="rounded-2xl p-5"
         style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
      <Icon className="w-6 h-6 mb-2" style={{ color: PALETTE.amber }} />
      <div className="font-display text-lg mb-1">{title}</div>
      <p className="text-sm opacity-80">{body}</p>
    </div>
  );
}


function PhaseCard({ n, title, body, color }) {
  return (
    <div className="rounded-2xl border bg-white p-5"
         style={{ borderColor: color }}>
      <div className="font-mono text-xs tracking-widest opacity-70">PHASE {n}</div>
      <div className="font-display text-xl mt-1 mb-1" style={{ color }}>{title}</div>
      <p className="text-sm opacity-80">{body}</p>
    </div>
  );
}


function Faq({ q, a }) {
  return (
    <div>
      <dt className="font-display text-lg">{q}</dt>
      <dd className="text-sm opacity-80 mt-1">{a}</dd>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}


// ── Suggest a Tree / Garden (community lead form) ────────────────
// Fields per user requirement:
//   • Address (geographical)
//   • Contact person's name
//   • Phone number
//   • Notes / Comments (multilanguage — user picks a language)
// Kept simple so a passer-by can submit a lead in under a minute.
const SUGGEST_LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "ml", label: "മലയാളം" },
  { code: "bn", label: "বাংলা" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "zh", label: "中文" },
  { code: "ar", label: "العربية" },
  { code: "ja", label: "日本語" },
  { code: "vi", label: "Tiếng Việt" },
];

function SuggestTreeOrGardenForm() {
  const [form, setForm] = useState({
    address: "", contact_name: "", phone: "",
    kind: "tree", fruit_type: "",
    city: "", state: "", country: "USA", postal_code: "",
    notes: "", lang: "en",
    submitter_name: "", submitter_email: "",
  });
  const [kinds, setKinds] = useState({});
  const [fruits, setFruits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/urban-harvest/stats`).then((r) => {
      setKinds(r.data.suggestion_kinds || {});
      setFruits(r.data.common_fruits || []);
    });
  }, []);

  const canSubmit = form.address.trim().length >= 4;

  async function submit(e) {
    e?.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        address:        form.address.trim(),
        contact_name:   form.contact_name.trim() || null,
        phone:          form.phone.trim() || null,
        fruit_type:     form.fruit_type.trim() || null,
        city:           form.city.trim() || null,
        state:          form.state.trim() || null,
        postal_code:    form.postal_code.trim() || null,
        notes:          form.notes.trim() || null,
        submitter_name: form.submitter_name.trim() || null,
        submitter_email:form.submitter_email.trim() || null,
      };
      const res = await axios.post(`${API}/api/urban-harvest/suggestions`, payload);
      setDone(res.data);
      toast.success("Thanks! Your suggestion was sent to the Urban Harvest team.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the suggestion. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border-2 bg-white p-6 sm:p-8"
           style={{ borderColor: PALETTE.orange }}
           data-testid="uh-suggest-done">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-8 h-8 flex-none" style={{ color: PALETTE.orange }} />
          <div>
            <div className="font-display text-2xl mb-1" style={{ color: PALETTE.ink }}>
              Suggestion received.
            </div>
            <p className="text-sm opacity-80 max-w-lg mb-3">{done.message}</p>
            <button type="button"
                    onClick={() => { setDone(null); setForm({ ...form, address: "", contact_name: "", phone: "", notes: "" }); }}
                    className="text-xs underline opacity-80 hover:opacity-100"
                    data-testid="uh-suggest-another">
              Suggest another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit}
          className="rounded-2xl border-2 bg-white p-5 sm:p-6 space-y-4"
          style={{ borderColor: PALETTE.orange }}
          data-testid="uh-suggest-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="What is it?">
          <select value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="input" data-testid="uh-suggest-kind">
            {Object.entries(kinds).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </Field>
        <Field label="Fruit / vegetable (optional)">
          <select value={form.fruit_type}
                  onChange={(e) => setForm({ ...form, fruit_type: e.target.value })}
                  className="input" data-testid="uh-suggest-fruit">
            <option value="">Choose one (optional)…</option>
            {fruits.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Address * (street, cross-street, landmark, or GPS)">
        <input type="text" required minLength={4} value={form.address}
               onChange={(e) => setForm({ ...form, address: e.target.value })}
               className="input"
               placeholder="1247 Oak Street, Sunnyvale, CA — big lemon tree by fence"
               data-testid="uh-suggest-address" />
      </Field>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="City">
          <input type="text" value={form.city}
                 onChange={(e) => setForm({ ...form, city: e.target.value })}
                 className="input" placeholder="Sunnyvale"
                 data-testid="uh-suggest-city" />
        </Field>
        <Field label="State / region">
          <input type="text" value={form.state}
                 onChange={(e) => setForm({ ...form, state: e.target.value })}
                 className="input" placeholder="California"
                 data-testid="uh-suggest-state" />
        </Field>
        <Field label="Postal code">
          <input type="text" value={form.postal_code}
                 onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                 className="input" placeholder="94087"
                 data-testid="uh-suggest-postal" />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Contact person's name (if known)">
          <input type="text" value={form.contact_name}
                 onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                 className="input" placeholder="Sam Patel"
                 data-testid="uh-suggest-contact-name" />
        </Field>
        <Field label="Contact phone (if known)">
          <input type="tel" value={form.phone}
                 onChange={(e) => setForm({ ...form, phone: e.target.value })}
                 className="input" placeholder="+1 555 123 4567"
                 data-testid="uh-suggest-phone" />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Comments language">
          <select value={form.lang}
                  onChange={(e) => setForm({ ...form, lang: e.target.value })}
                  className="input" data-testid="uh-suggest-lang">
            {SUGGEST_LANGS.map((l) =>
              <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-3">
          <Field label="Notes / comments (any language)">
            <textarea rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="input"
                      placeholder="Big Meyer lemon by the front fence — the owner is elderly and mentioned they can't reach the top branches."
                      data-testid="uh-suggest-notes" />
          </Field>
        </div>
      </div>

      <details className="text-sm opacity-80">
        <summary className="cursor-pointer" data-testid="uh-suggest-toggle-submitter">
          Add your name / email so the team can follow up (optional)
        </summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field label="Your name">
            <input type="text" value={form.submitter_name}
                   onChange={(e) => setForm({ ...form, submitter_name: e.target.value })}
                   className="input"
                   data-testid="uh-suggest-submitter-name" />
          </Field>
          <Field label="Your email">
            <input type="email" value={form.submitter_email}
                   onChange={(e) => setForm({ ...form, submitter_email: e.target.value })}
                   className="input"
                   data-testid="uh-suggest-submitter-email" />
          </Field>
        </div>
      </details>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="text-[10px] opacity-60 max-w-md">
          We never contact the property owner without their consent. Leads
          are only shared with the local Gleaning Corps or city partner and
          used to send a gentle invitation to join the registry.
        </div>
        <button type="submit" disabled={!canSubmit || busy}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-white font-semibold disabled:opacity-50 hover:shadow-md transition-shadow"
                style={{ background: PALETTE.orange }}
                data-testid="uh-suggest-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {busy ? "Sending…" : "Send suggestion"}
        </button>
      </div>

      <style>{`.input{width:100%;border:1px solid #C7B58A;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;background:#fff}`}</style>
    </form>
  );
}
